import React, { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import {
  Box,
  Container,
  Paper,
  Tabs,
  Tab,
  Typography,
  useTheme,
  useMediaQuery,
  Avatar,
  Grid,
  Divider,
  Fade,
  Card,
  IconButton,
  styled,
} from '@mui/material'
import { useTranslation } from 'react-i18next'

// Icons
import PersonIcon from '@mui/icons-material/Person'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import PaymentIcon from '@mui/icons-material/Payment'
import ColorLensIcon from '@mui/icons-material/ColorLens'
import LockIcon from '@mui/icons-material/Lock'
import NotificationsIcon from '@mui/icons-material/Notifications'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'

// Components
import Account from './User'
import AvailabilitySelector from './AvailabilitySelector'
import SubscriptionManager from './SubscriptionManager'

// Custom styled TabPanel component
const TabPanel = (props) => {
  const { children, value, index, ...other } = props

  return (
    <Fade in={value === index} timeout={500}>
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`settings-tabpanel-${index}`}
        aria-labelledby={`settings-tab-${index}`}
        {...other}
      >
        {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
      </div>
    </Fade>
  )
}

// Styled tab for better aesthetics
const StyledTab = styled(Tab)(({ theme }) => ({
  minHeight: 64,
  padding: '12px 16px',
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '0.95rem',
  textAlign: 'left',
  justifyContent: 'flex-start',
  alignItems: 'flex-start',
  borderRadius: theme.shape.borderRadius,
  '&.Mui-selected': {
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.action.selected,
  },
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}))

// Main component
export default function Settings() {
  const { t } = useTranslation()
  const [tabIndex, setTabIndex] = useState(0)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const location = useLocation()

  // Check for tab parameter from URL
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const tabParam = searchParams.get('tab')

    if (tabParam === 'availability') {
      setTabIndex(1)
    } else if (tabParam === 'subscription') {
      setTabIndex(2)
    }
  }, [location])

  const handleTabChange = (event, newIndex) => {
    setTabIndex(newIndex)
  }

  // Sample function to simulate additional tabs
  const renderPlaceholder = () => (
    <Box sx={{ p: 3, textAlign: 'center' }}>
      <Typography variant="h6" color="text.secondary" gutterBottom>
        This section is coming soon
      </Typography>
      <Typography variant="body2" color="text.secondary">
        We're working on making this feature available soon.
      </Typography>
    </Box>
  )

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, sm: 4 } }}>
      <Typography
        variant="h4"
        component="h1"
        gutterBottom
        sx={{
          mb: 4,
          fontWeight: 'bold',
          borderBottom: `1px solid ${theme.palette.divider}`,
          pb: 2,
        }}
      >
        {t('settings.title', 'Settings')}
      </Typography>

      <Grid container spacing={4}>
        {/* Left sidebar with tabs on desktop, horizontal tabs on mobile */}
        <Grid item xs={12} md={3}>
          <Paper
            elevation={1}
            sx={{
              borderRadius: 2,
              overflow: 'hidden',
              mb: { xs: 3, md: 0 },
            }}
          >
            {isMobile ? (
              <Tabs
                value={tabIndex}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                aria-label="settings tabs"
                sx={{
                  borderBottom: 1,
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                }}
              >
                <Tab
                  icon={<PersonIcon />}
                  iconPosition="start"
                  label={t('settings.tabs.profile')}
                  id="settings-tab-0"
                  aria-controls="settings-tabpanel-0"
                />
                <Tab
                  icon={<AccessTimeIcon />}
                  iconPosition="start"
                  label={t('settings.tabs.availability')}
                  id="settings-tab-1"
                  aria-controls="settings-tabpanel-1"
                />
                <Tab
                  icon={<PaymentIcon />}
                  iconPosition="start"
                  label={t('settings.tabs.subscription')}
                  id="settings-tab-2"
                  aria-controls="settings-tabpanel-2"
                />
              </Tabs>
            ) : (
              <Box sx={{ py: 1 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    px: 2,
                    py: 1,
                    color: 'text.secondary',
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                  }}
                >
                  {t('settings.personalSection', 'Personal')}
                </Typography>

                <StyledTab
                  fullWidth
                  icon={<PersonIcon sx={{ mr: 2 }} />}
                  iconPosition="start"
                  label={t('settings.tabs.profile')}
                  onClick={(e) => handleTabChange(e, 0)}
                  selected={tabIndex === 0}
                  sx={{ width: '100%' }}
                />

                <StyledTab
                  fullWidth
                  icon={<AccessTimeIcon sx={{ mr: 2 }} />}
                  iconPosition="start"
                  label={t('settings.tabs.availability')}
                  onClick={(e) => handleTabChange(e, 1)}
                  selected={tabIndex === 1}
                  sx={{ width: '100%' }}
                />

                <Divider sx={{ my: 1.5 }} />

                <Typography
                  variant="subtitle2"
                  sx={{
                    px: 2,
                    py: 1,
                    color: 'text.secondary',
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                  }}
                >
                  {t('settings.paymentsSection', 'Payments')}
                </Typography>

                <StyledTab
                  fullWidth
                  icon={<PaymentIcon sx={{ mr: 2 }} />}
                  iconPosition="start"
                  label={t('settings.tabs.subscription')}
                  onClick={(e) => handleTabChange(e, 2)}
                  selected={tabIndex === 2}
                  sx={{ width: '100%' }}
                />

                <Divider sx={{ my: 1.5 }} />

                <Typography
                  variant="subtitle2"
                  sx={{
                    px: 2,
                    py: 1,
                    color: 'text.secondary',
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                  }}
                >
                  {t('settings.preferencesSection', 'Preferences')}
                </Typography>

                <StyledTab
                  fullWidth
                  icon={<ColorLensIcon sx={{ mr: 2 }} />}
                  iconPosition="start"
                  label={t('settings.tabs.appearance', 'Appearance')}
                  onClick={(e) => handleTabChange(e, 3)}
                  selected={tabIndex === 3}
                  sx={{ width: '100%', opacity: 0.7 }}
                  disabled
                />

                <StyledTab
                  fullWidth
                  icon={<NotificationsIcon sx={{ mr: 2 }} />}
                  iconPosition="start"
                  label={t('settings.tabs.notifications', 'Notifications')}
                  onClick={(e) => handleTabChange(e, 4)}
                  selected={tabIndex === 4}
                  sx={{ width: '100%', opacity: 0.7 }}
                  disabled
                />

                <StyledTab
                  fullWidth
                  icon={<LockIcon sx={{ mr: 2 }} />}
                  iconPosition="start"
                  label={t('settings.tabs.security', 'Security')}
                  onClick={(e) => handleTabChange(e, 5)}
                  selected={tabIndex === 5}
                  sx={{ width: '100%', opacity: 0.7 }}
                  disabled
                />
              </Box>
            )}
          </Paper>

          {/* Quick links card */}
          {!isMobile && (
            <Card
              sx={{
                mt: 3,
                borderRadius: 2,
                overflow: 'hidden',
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Box sx={{ p: 2, bgcolor: 'primary.50' }}>
                <Typography variant="subtitle1" fontWeight={500}>
                  {t('settings.quickLinks', 'Quick Links')}
                </Typography>
              </Box>
              <Divider />
              <Box sx={{ p: 0 }}>
                {['Documents', 'Watchlist', 'Inbox'].map((item, index) => (
                  <Box
                    key={index}
                    sx={{
                      p: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: 'action.hover',
                      },
                      borderBottom: index < 2 ? `1px solid ${theme.palette.divider}` : 'none',
                    }}
                  >
                    <Typography variant="body2">
                      {t(`settings.quickLink.${item.toLowerCase()}`, item)}
                    </Typography>
                    <IconButton size="small">
                      <KeyboardArrowRightIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            </Card>
          )}
        </Grid>

        {/* Main content area */}
        <Grid item xs={12} md={9}>
          <Paper
            elevation={1}
            sx={{
              borderRadius: 2,
              overflow: 'hidden',
              minHeight: 400,
            }}
          >
            <TabPanel value={tabIndex} index={0}>
              <Account />
            </TabPanel>

            <TabPanel value={tabIndex} index={1}>
              <AvailabilitySelector />
            </TabPanel>

            <TabPanel value={tabIndex} index={2}>
              <SubscriptionManager />
            </TabPanel>

            <TabPanel value={tabIndex} index={3}>
              {renderPlaceholder()}
            </TabPanel>

            <TabPanel value={tabIndex} index={4}>
              {renderPlaceholder()}
            </TabPanel>

            <TabPanel value={tabIndex} index={5}>
              {renderPlaceholder()}
            </TabPanel>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  )
}
